import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { Flashlight, FlashlightOff, SwitchCamera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Notice } from '@/components/ui/Notice';
import { Spinner } from '@/components/ui/Spinner';
import {
  cameraProblem,
  createRepeatGuard,
  loadBarcodeReader,
  scannedCode,
  signalRead,
  type CameraProblem,
} from './camera-scan';

/** A read code: true to keep scanning, false when the screen must show something (CAM-03). */
export type CodeHandler = (code: string) => boolean | Promise<boolean>;

type Status =
  { kind: 'starting' } | { kind: 'scanning' } | { kind: 'error'; problem: CameraProblem };

/** Between two looks at the picture: fast enough to feel instant, light on the battery. */
const LOOK_EVERY_MS = 120;

const stopTracks = (stream: MediaStream) => stream.getTracks().forEach((track) => track.stop());

/** CAM-02: the back camera, a frame to aim with, and each read handed to `onCode`. */
export function CameraScanDialog({
  open,
  onClose,
  onCode,
}: {
  open: boolean;
  onClose: () => void;
  onCode: CodeHandler;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} title={t('camera.title')}>
      {open && <CameraView onCode={onCode} onClose={onClose} />}
    </Dialog>
  );
}

function CameraView({ onCode, onClose }: { onCode: CodeHandler; onClose: () => void }) {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const track = useRef<MediaStreamTrack | null>(null);
  const [status, setStatus] = useState<Status>({ kind: 'starting' });
  // A new camera or a retry starts the stream again.
  const [start, setStart] = useState<{ deviceId?: string; attempt: number }>({ attempt: 0 });
  const [cameras, setCameras] = useState<string[]>([]);
  // CAM-04: kept here, as iOS reports the torch's state wrong.
  const [torch, setTorch] = useState({ available: false, on: false });
  const [lastCode, setLastCode] = useState<string | null>(null);
  const handleCode = useEffectEvent(onCode);
  const close = useEffectEvent(onClose);

  useEffect(() => {
    let stopped = false;
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    const seen = createRepeatGuard();

    const run = async () => {
      try {
        const reader = loadBarcodeReader();
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: start.deviceId
            ? { deviceId: { exact: start.deviceId } }
            : {
                facingMode: { ideal: 'environment' },
                width: { ideal: 1280 },
                height: { ideal: 720 },
              },
        });
        const element = video.current;
        if (stopped || !element) return;
        element.srcObject = stream;
        await element.play();
        track.current = stream.getVideoTracks()[0] ?? null;
        const capabilities = track.current?.getCapabilities?.() as
          (MediaTrackCapabilities & { torch?: boolean }) | undefined;
        setTorch({ available: capabilities?.torch === true, on: false });
        // Labelled and complete only once the camera is allowed.
        const devices = await navigator.mediaDevices.enumerateDevices();
        setCameras(
          devices.flatMap((d) => (d.kind === 'videoinput' && d.deviceId ? [d.deviceId] : [])),
        );
        const detector = await reader;
        if (stopped) return;
        setStatus({ kind: 'scanning' });

        const look = async () => {
          if (stopped) return;
          if (element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            const found = await detector.detect(element).catch(() => []);
            const code = seen(found.map(scannedCode).filter(Boolean), Date.now())[0];
            if (code && !stopped) {
              signalRead();
              setLastCode(code);
              if (!(await handleCode(code))) {
                close();
                return;
              }
            }
          }
          if (!stopped) timer = window.setTimeout(() => void look(), LOOK_EVERY_MS);
        };
        void look();
      } catch (error) {
        if (!stopped) setStatus({ kind: 'error', problem: cameraProblem(error) });
      }
    };
    void run();

    return () => {
      stopped = true;
      window.clearTimeout(timer);
      if (stream) stopTracks(stream);
      track.current = null;
    };
  }, [start]);

  const restart = (deviceId?: string) => {
    setStatus({ kind: 'starting' });
    setTorch({ available: false, on: false });
    setStart((current) => ({ deviceId, attempt: current.attempt + 1 }));
  };

  const switchCamera = () => {
    const current = track.current?.getSettings().deviceId ?? start.deviceId;
    const next = cameras[(cameras.indexOf(current ?? '') + 1) % cameras.length];
    restart(next);
  };

  const toggleTorch = async () => {
    const on = !torch.on;
    try {
      await track.current?.applyConstraints({
        advanced: [{ torch: on } as MediaTrackConstraintSet],
      });
      setTorch({ available: true, on });
    } catch {
      setTorch({ available: false, on: false });
    }
  };

  if (status.kind === 'error') {
    return (
      <div className="grid gap-4">
        <Notice tone="danger" role="alert" title={t(`camera.problems.${status.problem}.title`)}>
          {t(`camera.problems.${status.problem}.text`)}
        </Notice>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('camera.done')}
          </Button>
          <Button onClick={() => restart(start.deviceId)}>{t('camera.retry')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      <div className="relative aspect-[4/3] overflow-hidden rounded-control bg-ink">
        <video
          ref={video}
          muted
          playsInline
          autoPlay
          aria-hidden="true"
          className="size-full object-cover"
        />
        <div
          aria-hidden="true"
          className="absolute inset-x-[10%] top-1/2 h-2/5 -translate-y-1/2 rounded-control border-2 border-white/90 shadow-[0_0_0_100vmax_rgb(0_0_0/0.25)]"
        />
        {status.kind === 'starting' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner className="size-8 text-white" label={t('camera.starting')} />
          </div>
        )}
      </div>
      <p role="status" className="min-h-6 text-center text-ink-2">
        {status.kind === 'starting'
          ? t('camera.starting')
          : lastCode
            ? t('camera.read', { code: lastCode })
            : t('camera.aim')}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          {torch.available && (
            <Button
              variant="secondary"
              icon={
                torch.on ? <FlashlightOff className="size-5" /> : <Flashlight className="size-5" />
              }
              onClick={() => void toggleTorch()}
              aria-pressed={torch.on}
            >
              {t('camera.light')}
            </Button>
          )}
          {cameras.length > 1 && (
            <Button
              variant="secondary"
              icon={<SwitchCamera className="size-5" />}
              onClick={switchCamera}
            >
              {t('camera.switch')}
            </Button>
          )}
        </div>
        <Button onClick={onClose}>{t('camera.done')}</Button>
      </div>
    </div>
  );
}
